import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useIonToast,
  IonSpinner,
  IonAlert,
  isPlatform,
  IonList,
} from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";
import { IconPlus, IconSearch, IconX } from "@tabler/icons-react";

import CrawlingApi from "../../../api/crawling";

import { useCrawlingActions, useCrawlingData } from "../../../store/crawling";
import AiApi from "../../../api/ai";
import SkeletonText from "../../SkeletonText";
import Url, { WhileCrawling } from "./url";

import Alert from "../../common/alert";
import normalizeUrl from "./utils";

import globalOutboundStyles from "../../outbound/styles.module.css";
import styles from "./styles.module.css";
import { useAppStore } from "../../../store/app";

const MAX_ACTIVE_URLS = 250;

export default function CrawlWebsite() {
  const isMobile = isPlatform("android") || isPlatform("ios");
  const [website, setWebsite] = React.useState<string>();
  const [singleWebsite, setSingleWebsite] = React.useState<string>("");
  const [openAlert, setOpenAlert] = React.useState(false);
  const [currentEmbedding, setCurrentEmbedding] = React.useState<string>();
  const [alertType, setAlertType] = React.useState<"delete" | "recrawl">();
  const [showInput, setShowInput] = React.useState(false);

  const [present] = useIonToast();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const company = useAppStore((state) => state.user.company);

  const { fullCrawl, singleCrawl } = useCrawlingData();
  const { setFullCrawl, setSingleCrawl } = useCrawlingActions();
  const { urls, isScanning, isOptimizing } = fullCrawl;

  const needsCrawling = !urls || urls?.length === 0;
  const isMaxUrls = React.useMemo(() => {
    const activeFullCrawl = fullCrawl?.urls?.filter((u) => u.active)?.length;
    const activeSingleCrawl = singleCrawl?.filter((u) => u.active)?.length;

    return (activeFullCrawl || 0) + (activeSingleCrawl || 0) >= MAX_ACTIVE_URLS;
  }, [fullCrawl?.urls, singleCrawl]);

  const setUrlUpdate = (message: any) => {
    const isSingleCrawl = message.source === "single-crawl";
    const currentArr = isSingleCrawl ? singleCrawl : fullCrawl?.urls || [];

    const indexToUpdate = currentArr.findIndex(
      (u) => normalizeUrl(u.url) === normalizeUrl(message.url)
    );
    if (indexToUpdate === -1) {
      const updatedArr = [
        ...currentArr,
        { ...message, active: message.completed },
      ];
      return isSingleCrawl
        ? setSingleCrawl(updatedArr)
        : setFullCrawl({ urls: updatedArr });
    }

    const updatedArr = [...currentArr];
    updatedArr[indexToUpdate] = {
      ...updatedArr[indexToUpdate],
      ...message,
      active: message.completed,
    };

    if (message.completed) {
      present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    }

    return isSingleCrawl
      ? setSingleCrawl(updatedArr)
      : setFullCrawl({ urls: updatedArr });
  };

  const {
    data: aiData,
    refetch: refetchAi,
    isLoading: isGettingAi,
  } = useQuery({
    queryFn: () => AiApi.getWebsite(),
    queryKey: ["getWebsite"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const mutateDeleteUrl = useMutation({
    mutationFn: AiApi.deleteUrl,
    onSuccess: async () => {
      setCurrentEmbedding(undefined);
      await queryClient.invalidateQueries({ queryKey: ["getWebsite"] });
      return present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    },
    onError: () => {
      setCurrentEmbedding(undefined);
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 5000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const mutateActivateUrl = useMutation({
    mutationFn: AiApi.patchUrl,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getWebsite"],
      });
      return present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 5000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const { mutate: mutateNewCrawling, isPending: isCreatingEvent } = useMutation(
    {
      mutationFn: CrawlingApi.newCrawlingEvent,
      onSuccess: async ({ data }) => {
        if (data && data?.ok) {
          setFullCrawl({ isScanning: true });
          return await queryClient.invalidateQueries({
            queryKey: ["getAiStatus"],
          });
        }
      },
      onError: () => {
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 6000,
          color: "light",
          position: "top",
          cssClass: "error-toast",
        });
      },
    }
  );

  const mutateResetCrawling = useMutation({
    mutationFn: AiApi.resetCrawling,
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["getWebsite"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["getAiStatus"],
        }),
      ]);
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 5000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const mutateSingleCrawling = useMutation({
    mutationFn: AiApi.singleCrawling,
    onSuccess: (data, variables) => {
      if (data && data?.success) {
        setSingleWebsite("");
        setShowInput(false);
        return setUrlUpdate({ ...variables, completed: false, error: false });
      }
    },
    onError: () => {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    },
  });

  const onSubmitCrawling = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (website) {
      if (needsCrawling) {
        return mutateNewCrawling({ companyId: company.id, url: website });
      }
      setAlertType(() => "recrawl");
      return setOpenAlert(true);
    }
  };

  const onSubmitSingleCrawling = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (singleWebsite && singleWebsite.length > 0) {
      return mutateSingleCrawling.mutate({
        companyId: String(company.id),
        url: singleWebsite,
        // @ts-ignore,
        source: "single-crawl",
      });
    }
  };

  const onBeforeDeleteEmbedding = (embeddingId: string) => {
    setCurrentEmbedding(embeddingId);
    setAlertType(() => "delete");
    setOpenAlert(true);
  };

  const onDeleteEmbedding = () => {
    setOpenAlert(false);
    setAlertType(undefined);
    return mutateDeleteUrl.mutate({ embeddingId: currentEmbedding });
  };

  const onBeforeResetCrawling = () => {
    if (website) {
      setOpenAlert(false);
      setAlertType(undefined);
      return mutateResetCrawling.mutate({
        companyId: company.id,
        url: website,
      });
    }
  };

  const onHideAlert = () => {
    setCurrentEmbedding(undefined);
    setAlertType(undefined);
    return setOpenAlert(() => false);
  };

  React.useEffect(() => {
    const embeddings = aiData?.embeddings;
    if (!embeddings) return;

    const { fullCrawl, singleCrawl } = embeddings;

    const updatedFullCrawl =
      urls && urls.length > 0 && fullCrawl?.length === 0 ? urls : fullCrawl;

    setFullCrawl({ urls: updatedFullCrawl });
    setSingleCrawl(singleCrawl);
  }, [aiData]);

  React.useEffect(() => {
    if (!website && website !== "" && company?.website) {
      setWebsite(company.website);
    }
  }, [website, company?.website]);

  const isRequesting =
    isCreatingEvent ||
    isScanning ||
    isOptimizing ||
    mutateResetCrawling.isPending ||
    fullCrawl?.urls?.[0]?.completed === false;

  const isFetchingData = isGettingAi || mutateDeleteUrl.isPending;
  const isDeleteAlert = alertType === "delete";
  const hasFullCrawling = urls && urls?.length > 0;
  const hasSingleCrawling = singleCrawl && singleCrawl?.length > 0;

  const hasAnyCrawling = hasFullCrawling || hasSingleCrawling;

  return isFetchingData ? (
    isMobile ? (
      <div className="p-3 flex-align-center justify-content-center">
        <IonSpinner name="lines-sharp"></IonSpinner>
      </div>
    ) : (
      <div className="px-4 w-100 h-100 flex flex-column">
        <SkeletonText rows={15} />
      </div>
    )
  ) : (
    <div
      className={
        isMobile ? `` : "flex flex-column gap-4 w-100 h-100 overflow-y-none pb-4 px-4"
      }
    >
      {isMaxUrls && (
        <div className="flex flex-column gap-3">
          <Alert
            type="danger"
            className="p-3"
            message={intl.formatMessage(
              { id: "ai.sources.website.max" },
              { limit: MAX_ACTIVE_URLS }
            )}
          />
        </div>
      )}
      <div
        className={`${styles.fullCrawlingContainer} flex flex-column gap-3 w-100`}
      >
        {hasAnyCrawling && (
          <div className="flex align-items-center gap-3 w-100">
            <hr className={styles.divider} />
            <p className="w-max mb-1 text-nowrap description">
              <FormattedMessage id="ai.sources.website.fullCrawlTitle" />
            </p>
            <hr className={styles.divider} />
          </div>
        )}

        {!isMaxUrls && !isRequesting && (
          <div
            className={isMobile ? `view-pane` : "flex flex-column gap-1 w-100"}
          >
            <form
              onSubmit={onSubmitCrawling}
              className="flex flex-row gap-2 w-100"
            >
              <input
                type="text"
                className="form-control no-focus"
                value={website || ""}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="off"
                required
              />
              <button
                className="btn btn-visito-primary btn-sm w-20"
                type="submit"
              >
                {isMobile ? (
                  <IconSearch size={18} />
                ) : hasFullCrawling ? (
                  <FormattedMessage id="common.rescan" />
                ) : (
                  <FormattedMessage id="common.scan" />
                )}
              </button>
            </form>
            {hasAnyCrawling && (
              <span className="description fs-2">
                <FormattedMessage id="ai.sources.website.fullCrawl" />
              </span>
            )}
          </div>
        )}
        {isRequesting &&
          (isMobile ? (
            <div className="view-pane">
              <Alert
                type="info"
                message={intl.formatMessage({
                  id: "ai.sources.website.scanning.info",
                })}
                className="p-2"
              />
            </div>
          ) : (
            <Alert
              type="info"
              message={intl.formatMessage({
                id: "ai.sources.website.scanning.info",
              })}
              className="p-2"
            />
          ))}

        {hasFullCrawling ? (
          isMobile ? (
            <IonList inset={true}>
              {urls.map((u) => (
                <Url
                  url={u}
                  key={u.url}
                  isCrawling={isRequesting}
                  onDelete={onBeforeDeleteEmbedding}
                  onEnableUrl={mutateActivateUrl.mutate}
                  onRescanUrl={(data) =>
                    mutateSingleCrawling.mutate({
                      ...(data as any),
                      companyId: String(company.id),
                    })
                  }
                />
              ))}
            </IonList>
          ) : (
            <div className={styles.siteMapContainer}>
              <div className={`${styles.linksContainer} flex flex-column`}>
                {urls.map((u) => (
                  <Url
                    url={u}
                    key={u.url}
                    isCrawling={isRequesting}
                    onDelete={onBeforeDeleteEmbedding}
                    onEnableUrl={mutateActivateUrl.mutate}
                    onRescanUrl={(data) =>
                      mutateSingleCrawling.mutate({
                        ...(data as any),
                        companyId: String(company.id),
                      })
                    }
                  />
                ))}
              </div>
            </div>
          )
        ) : null}

        {!hasFullCrawling && isRequesting && (
          <WhileCrawling url={website || ""} />
        )}
      </div>

      {hasAnyCrawling && (
        <div
          className={`${styles.singleCrawlingContainer} flex flex-column gap-3 w-100`}
        >
          <div className={isMobile ? "view-pane" : "flex flex-column w-100"}>
            <div className="flex align-items-center gap-3 w-100">
              <hr className={styles.divider} />
              <p className="w-max mb-1 text-nowrap description">
                <FormattedMessage id="common.add" />{" "}
                <FormattedMessage id="ai.sources.website.singleCrawlTitle" />
              </p>

              <hr className={styles.divider} />
            </div>

            {!isMaxUrls && (
              <div className="flex flex-column gap-1 w-100">
                {showInput || !hasSingleCrawling ? (
                  <>
                    <form
                      onSubmit={onSubmitSingleCrawling}
                      className="flex flex-row gap-2 w-100 mt-2"
                    >
                      <input
                        type="text"
                        className="form-control no-focus"
                        value={singleWebsite}
                        onChange={(e) => setSingleWebsite(e.target.value)}
                        disabled={mutateSingleCrawling.isPending}
                        autoComplete="off"
                        placeholder="https://example.com/page"
                        required
                        autoFocus
                      />
                      <button
                        className="btn btn-visito-primary btn-sm"
                        disabled={isRequesting}
                        type="submit"
                      >
                        {mutateSingleCrawling.isPending ? (
                          <IonSpinner
                            name="circular"
                            style={{
                              color: "var(--ion-color-light)",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        ) : (
                          <FormattedMessage id="common.add" />
                        )}
                      </button>

                      {hasSingleCrawling ? (
                        <button
                          className="btn btn-visito-outline btn-sm btn-visito-outline-no-hover"
                          onClick={() => setShowInput(false)}
                          type="button"
                        >
                          <IconX size={18} />
                        </button>
                      ) : null}
                    </form>
                    <div>
                      <span className="description fs-2">
                        <FormattedMessage id="ai.sources.website.singleCrawl" />
                      </span>
                    </div>
                  </>
                ) : (
                  <button
                    className="btn btn-visito-outline btn-sm w-auto ml-auto"
                    onClick={() => setShowInput(true)}
                  >
                    <IconPlus size={20} />
                  </button>
                )}
              </div>
            )}
          </div>

          {isRequesting &&
            (isMobile ? (
              <div className="view-pane">
                <Alert
                  type="info"
                  message={intl.formatMessage({
                    id: "ai.sources.website.scanning.info",
                  })}
                  className="p-2"
                />
              </div>
            ) : null)}

          {hasSingleCrawling ? (
            isMobile ? (
              <IonList inset={true}>
                {singleCrawl.map((u) => (
                  <Url
                    url={u}
                    key={u.url}
                    isCrawling={isRequesting}
                    onDelete={onBeforeDeleteEmbedding}
                    onEnableUrl={mutateActivateUrl.mutate}
                    onRescanUrl={(data) =>
                      mutateSingleCrawling.mutate({
                        ...(data as any),
                        companyId: String(company.id),
                      })
                    }
                  />
                ))}
              </IonList>
            ) : (
              <div className={styles.siteMapContainer}>
                <div className={`${styles.linksContainer} flex flex-column`}>
                  {singleCrawl.map((u) => (
                    <Url
                      url={u}
                      key={u.url}
                      isCrawling={isRequesting}
                      onDelete={onBeforeDeleteEmbedding}
                      onEnableUrl={mutateActivateUrl.mutate}
                      onRescanUrl={(data) =>
                        mutateSingleCrawling.mutate({
                          ...(data as any),
                          companyId: String(company.id),
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            )
          ) : null}
        </div>
      )}

      <IonAlert
        isOpen={openAlert}
        header={intl.formatMessage({
          id: isDeleteAlert
            ? "ai.sources.website.delete.confirmation"
            : "ai.sources.website.rescan.confirmation",
        })}
        message={intl.formatMessage({
          id: isDeleteAlert
            ? "ai.sources.website.delete.confirmationDesc"
            : "ai.sources.website.delete.confirmationDesc",
        })}
        buttons={[
          {
            text: intl.formatMessage({ id: "common.cancel" }),
            role: "cancel",
            handler: onHideAlert,
            cssClass: `${globalOutboundStyles.alertCancel} ${globalOutboundStyles.alertBtn}`,
          },
          {
            text: intl.formatMessage({
              id: isDeleteAlert ? "common.confirmDelete" : "common.confirmScan",
            }),
            role: "confirm",
            handler: isDeleteAlert ? onDeleteEmbedding : onBeforeResetCrawling,
            cssClass: `${globalOutboundStyles.alertConfirm} ${globalOutboundStyles.alertBtn}`,
          },
        ]}
        onDidDismiss={onHideAlert}
      />
    </div>
  );
}
